import * as React from "react";
import { tv } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { sectionWrapper } from "./sectionWrappers";
import { ComponentProps } from "react";
import { getEditableAttrs } from "./utils";
import Image from "next/image";
import { parseAiAlt } from "@repo/ai-disclaimer";
import { AiDisclaimerForAlt } from "./AiDisclaimer";
import { SectionTitleProps, SectionTitle } from "./SectionTitle";
import { CardPlans, CardPlansProps } from "./CardPlans";
import { CardFeatures, CardFeaturesProps } from "./CardFeatures";
import { IMAGE_QUALITY } from "./constants";
import { LocationSearch, LocationSearchProps } from "./LocationSearch";

import {
  ImageProps,
  ImageWithAspectRatioProps,
  SectionBackgroundColorType,
  SectionTopPaddingSizeType,
  SectionBottomPaddingSizeType,
  BackgroundOverlayOptionType,
  BackgroundOverlayColorType,
} from "./types";
import { ImageWithAspectRatio } from "./ImageWithAspectRatio";

interface ImageCardProps extends ImageWithAspectRatioProps {
  imageSpace?: "fit" | "fill";
}

const backgroundOverlayStyle = tv({
  base: "uipm:absolute uipm:inset-0",
  variants: {
    overlayColor: {
      primary: "uipm:bg-[var(--surface-surface-accent)]",
      secondary: "uipm:bg-[var(--surface-surface-accent-secondary)]",
      "accent-light": "uipm:bg-[var(--surface-surface-accent-light)]",
    },
    overlayOpacity: {
      "brand-10": "uipm:opacity-10",
      "brand-20": "uipm:opacity-20",
      "brand-30": "uipm:opacity-30",
      "brand-40": "uipm:opacity-40",
      "brand-50": "uipm:opacity-50",
      "brand-60": "uipm:opacity-60",
      "brand-70": "uipm:opacity-70",
      "brand-80": "uipm:opacity-80",
      "brand-90": "uipm:opacity-90",
      "brand-full": "uipm:opacity-100",
      "brand-empty": "uipm:opacity-0",
    },
  },
  defaultVariants: {
    overlayColor: "primary",
    overlayOpacity: "brand-50",
  },
});

const heroWrapper = tv({
  base: cn("uipm:relative uipm:w-full uipm:font-[var(--font-family-sans)]"),
  variants: {
    hasBackgroundImage: {
      true: "",
      false: "",
    },
    backgroundColor: {
      none: "",
      light: "",
      accent: "",
      "accent-light": "",
    },
  },
  compoundVariants: [
    {
      hasBackgroundImage: false,
      backgroundColor: "none",
      class: "uipm:bg-[var(--surface-surface-primary)]",
    },
    {
      hasBackgroundImage: false,
      backgroundColor: "light",
      class: "uipm:bg-[var(--surface-surface-secondary)]",
    },
    {
      hasBackgroundImage: false,
      backgroundColor: "accent",
      class: "uipm:bg-[var(--surface-surface-accent-secondary)]",
    },
    {
      hasBackgroundImage: false,
      backgroundColor: "accent-light",
      class: "uipm:bg-[var(--surface-surface-accent-light)]",
    },
  ],
});

const imageSection = tv({
  base: cn(
    "uipm:relative uipm:flex-shrink-0",
    // Mobile: full width
    "uipm:w-full",
    // Desktop: fixed width 420px
  ),
  variants: {
    imageHorizontalAlignment: {
      left: "uipm:items-start",
      middle: "uipm:items-center",
      right: "uipm:items-end",
    },
    imageVerticalAlignment: {
      top: "uipm:justify-start",
      middle: "uipm:justify-center",
      bottom: "uipm:justify-end",
    },
  },
  defaultVariants: {
    imageHorizontalAlignment: "middle",
    imageVerticalAlignment: "middle",
  },
});

type CardType =
  | CardPlansProps
  | CardFeaturesProps
  | ImageCardProps
  | React.ReactNode;

export interface HeroWideProps
  extends Omit<ComponentProps<"section">, "title"> {
  image?: ImageProps | null;
  imageSpace?: "fill" | "fit";
  enableOverlay?: boolean;
  backgroundOverlay?: BackgroundOverlayOptionType;
  backgroundOverlayColor?: BackgroundOverlayColorType;
  imageVerticalAlignment?: "top" | "middle" | "bottom";
  imageHorizontalAlignment?: "left" | "middle" | "right";
  typographyPosition?: "top" | "center" | "bottom";
  layout?: "default" | "reversed";
  sectionTitle?: SectionTitleProps;
  cards?: Array<{
    type: "plans" | "features" | "image" | "form";
    props: CardType;
    editableProps?: Record<string, unknown>;
  }>;
  backgroundColor?: SectionBackgroundColorType;
  topPadding?: SectionTopPaddingSizeType;
  bottomPadding?: SectionBottomPaddingSizeType;
  variant?: "fit" | "fixed";
  pageType?: "default" | "location";
  showLocationSearch?: boolean;
  locationSearchProps?: Omit<
    LocationSearchProps,
    "backgroundColor" | "topPadding" | "bottomPadding" | "pageType"
  >;
  sectionFixedHeight?: string;
}

export const HeroWide: React.FC<HeroWideProps> = ({
  image,
  imageSpace = "fill", // eslint-disable-line @typescript-eslint/no-unused-vars
  enableOverlay = true,
  backgroundOverlay = "brand-50",
  backgroundOverlayColor = "primary",
  imageVerticalAlignment = "middle",
  imageHorizontalAlignment = "middle",
  typographyPosition = "center",
  layout = "default",
  sectionTitle,
  cards,
  backgroundColor = "none",
  topPadding = "none",
  bottomPadding = "none",
  variant = "fit",
  pageType = "default",
  showLocationSearch = false,
  locationSearchProps,
  sectionFixedHeight,
  className,
  id,
  ...props
}) => {
  const hasBackgroundImage = !!image;
  const isLocationPage = pageType === "location";
  const hasFormCard = cards?.some((card) => card.type === "form");
  // 3-card layout: the two-column (text | cards) split only gives the cards
  // ~50% of the row, which fits 2 cards but not 3. For 3+ plan-style cards,
  // stack the text on top and let the cards span the full width at lg so all
  // three are visible. Scoped strictly to 3-up: 1/2-card, form, location, and
  // location-search layouts are untouched.
  const isThreeUp =
    !showLocationSearch &&
    !isLocationPage &&
    !hasFormCard &&
    (cards?.length ?? 0) >= 3;

  return (
    <section
      id={id}
      className={sectionWrapper({
        backgroundColor,
        topPadding,
        bottomPadding,
        pageType: "default",
      })}
      {...props}
    >
      <div
        className={cn(
          heroWrapper({ hasBackgroundImage, backgroundColor }),
          showLocationSearch && "uipm:overflow-visible",
          className,
        )}
      >
        {/* Background Image */}
        {image && (
          <div className="uipm:absolute uipm:inset-0">
            <Image
              src={image.src}
              alt={parseAiAlt(image.alt).cleanAlt}
              fill
              quality={IMAGE_QUALITY}
              className={cn("uipm:object-cover uipm:w-full uipm:h-full")}
              sizes="100vw"
              fetchPriority="high"
              priority
              // TODO: use preload with nextjs 16
              // preload
            />
            <AiDisclaimerForAlt alt={image.alt} src={image.src} />
          </div>
        )}

        {/* Background Overlay */}
        {image && (
          <div
            className={backgroundOverlayStyle({
              overlayColor: backgroundOverlayColor,
              overlayOpacity: backgroundOverlay,
            })}
          />
        )}

        {/* Bottom Gradient Overlay */}
        {image && enableOverlay && (
          <div
            className="uipm:absolute uipm:inset-0 uipm:pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 50%, rgba(0, 0, 0, 0.25) 113.25%)",
            }}
          />
        )}

        {/* Main Container */}
        <div
          className={cn(
            "uipm:max-w-[1440px] uipm:mx-auto",
            isLocationPage && "uipm:lg:max-w-[920px]",
          )}
        >
          <div
            className={cn(
              "uipm:relative uipm:flex uipm:flex-col",
              typographyPosition === "top" && "uipm:justify-start",
              typographyPosition === "center" && "uipm:justify-center",
              typographyPosition === "bottom" && "uipm:justify-end",
              !!sectionFixedHeight && "uipm:min-h-[400px]",
              "uipm:py-[var(--container-card-large-padding)] uipm:px-[var(--container-margin)]",
              "uipm:md:p-[var(--container-card-padding)]",
              `uipm:lg:p-[var(--container-card-large-padding)] ${isLocationPage ? "uipm:lg:px-0" : "uipm:lg:px-[var(--container-margin)]"}`,
              // Only add gap if there are cards (two columns layout)
              cards &&
                cards.length > 0 &&
                "uipm:lg:gap-[var(--container-gutter)]",

              showLocationSearch
                ? "uipm:lg:flex-col uipm:lg:items-center"
                : isLocationPage && (!cards || cards.length === 0)
                  ? "uipm:lg:flex-col"
                  : isThreeUp
                    ? "uipm:lg:flex-col"
                    : "uipm:lg:flex-row",
              !showLocationSearch &&
                typographyPosition === "top" &&
                "uipm:lg:items-start",
              !showLocationSearch &&
                typographyPosition === "center" &&
                "uipm:lg:items-center",
              !showLocationSearch &&
                typographyPosition === "bottom" &&
                "uipm:lg:items-end",
              // 3-up stacks vertically at lg, so children must fill the width
              // (override the two-column cross-axis alignment above). Also pin
              // the stack to the top: once flex-col, the base justify-* would
              // act on the vertical axis and center/bottom-gap a fixed-height hero.
              isThreeUp && "uipm:lg:items-stretch uipm:lg:justify-start",
            )}
            style={
              sectionFixedHeight && variant === "fixed"
                ? { minHeight: `${sectionFixedHeight}px` }
                : undefined
            }
          >
            {/* Cards Section - Shows first on mobile/tablet (unless it's a form) - Hidden when showLocationSearch */}
            {cards && cards.length > 0 && !showLocationSearch && (
              <div
                className={cn(
                  "uipm:relative uipm:z-10",
                  "uipm:w-full",
                  hasFormCard ? "uipm:order-2" : "uipm:order-1", // Second on mobile if it's a form, otherwise first
                  layout === "default" ? "uipm:lg:order-2" : "uipm:lg:order-1", // Second on desktop when layout is default
                  hasFormCard
                    ? "uipm:mt-[var(--container-title-gap)] uipm:lg:mt-0"
                    : "uipm:mb-[var(--container-title-gap)] uipm:lg:mb-0",
                  // Two-column: cards share the row (flex-1). 3-up: cards stack
                  // below the text and take the full width.
                  isThreeUp ? "uipm:lg:w-full" : "uipm:lg:flex-1",
                  "uipm:flex uipm:flex-col",
                )}
              >
                <div
                  className={cn(
                    "uipm:flex uipm:gap-[var(--container-gutter)] ",
                    "uipm:h-full",
                    // Horizontal swipe/scroll at every breakpoint: cards grow to
                    // fill when they fit (md:flex-1) and scroll when they don't
                    // (min-w-[320px]), so a 3rd card never clips on smaller screens.
                    "uipm:overflow-x-auto uipm:scrollbar-hide",
                    // Exception for form cards: their fields render downward-opening,
                    // absolutely-positioned (non-portalled) popups — a Select menu and
                    // the DatePicker calendar. overflow-x:auto forces overflow-y to
                    // compute to auto, which would clip those popups. Restore
                    // overflow-visible at md/lg (as before) for form-card rows; forms
                    // are full-width and don't need horizontal scroll.
                    hasFormCard &&
                      "uipm:md:overflow-visible uipm:lg:overflow-visible",
                  )}
                >
                  {cards.slice(0, 3).map((card, index) => (
                    <div
                      key={index}
                      {...(card.editableProps
                        ? card.editableProps
                        : card.type !== "form"
                          ? getEditableAttrs(card.props)
                          : {})}
                      className={cn(
                        "uipm:flex-shrink-0",
                        "uipm:flex uipm:flex-col",
                        card.type === "form"
                          ? "uipm:w-full uipm:md:flex-1 uipm:lg:flex-1"
                          : `${cards.length === 1 ? "uipm:w-full" : "uipm:w-[90%]"} uipm:min-w-[320px] uipm:md:w-full uipm:md:flex-1 uipm:lg:w-full uipm:lg:flex-1"}`,
                      )}
                    >
                      {card.type === "plans" ? (
                        <CardPlans
                          {...(card.props as CardPlansProps)}
                          className="uipm:rounded-[var(--radius-lg)]"
                        />
                      ) : card.type === "features" ? (
                        <CardFeatures
                          {...(card.props as CardFeaturesProps)}
                          className="uipm:rounded-[var(--radius-lg)]"
                        />
                      ) : card.type === "form" ? (
                        (card.props as React.ReactNode)
                      ) : (
                        <div
                          className={cn(
                            imageSection({
                              imageHorizontalAlignment,
                              imageVerticalAlignment,
                            }),
                            "uipm:w-full uipm:flex uipm:flex-col",
                          )}
                        >
                          {card.props && card.type === "image" && (
                            <ImageWithAspectRatio
                              {...({
                                ...(card.props as ImageCardProps),
                                sizes: `(min-width: 1440px) 656px, (min-width: 1024px) 50vw, calc(100vw - 32px)`,
                                priority: true,
                                fetchPriority: "high",
                              } as ImageCardProps)}
                              borderRadius="var(--radius-lg)"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Typography Section - Shows second on mobile/tablet (unless there's a form, then first) */}
            <div
              className={cn(
                "uipm:relative uipm:z-10 uipm:flex uipm:flex-col",
                "uipm:w-full",
                // When showLocationSearch, always order-1 and center with max-width
                showLocationSearch
                  ? "uipm:order-1 uipm:lg:order-1 uipm:lg:max-w-[920px] uipm:lg:mx-auto uipm:lg:text-center"
                  : cn(
                      hasFormCard ? "uipm:order-1" : "uipm:order-2", // First on mobile if there's a form, otherwise second
                      layout === "default"
                        ? "uipm:lg:order-1"
                        : "uipm:lg:order-2",
                      isThreeUp
                        ? "uipm:lg:w-full"
                        : cards && cards.length > 0
                          ? "uipm:lg:flex-1"
                          : isLocationPage
                            ? "uipm:lg:max-w-full"
                            : "uipm:lg:max-w-[50%]",
                    ),
                // Only add right padding when there are cards (two column layout)
                !showLocationSearch &&
                  cards &&
                  cards.length > 0 &&
                  "uipm:md:pr-[var(--padding-padding-default)]",
                // No right padding when 3-up: text is full-width above the cards,
                // not in a left column beside them.
                !showLocationSearch &&
                  !isThreeUp &&
                  cards &&
                  cards.length > 0 &&
                  "uipm:lg:pr-[var(--container-card-medium-padding)]",
                showLocationSearch && "uipm:overflow-visible",
              )}
            >
              {sectionTitle && (
                <SectionTitle
                  {...sectionTitle}
                  className={cn(
                    "uipm:max-w-[790px]",
                    image
                      ? "uipm:text-[var(--typography-colors-text-inverted)]"
                      : "",
                    sectionTitle.className,
                  )}
                />
              )}

              {/* Location Search */}
              {showLocationSearch && (
                <div className="uipm:mt-8 uipm:w-full uipm:flex uipm:justify-center">
                  <div className="uipm:w-full">
                    <LocationSearch
                      {...locationSearchProps}
                      backgroundColor="none"
                      topPadding="none"
                      bottomPadding="none"
                      pageType={pageType}
                      embedded={true}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
